import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

/**
 * VancouverCoastalWildlifeArticle
 *
 * A thorough guide to Vancouver's beaches for coastal wildlife observation.
 * Includes essential location markers, references, FAQs, and more.
 */
export default function VancouverCoastalWildlifeArticle() {
  // 1) Provide a unique reference ID (slug)
  const ref_id = "vancouver-beaches-coastal-wildlife";

  // 2) Access articles from your context
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "spanish-banks": "Spanish Banks",
    "jericho-beach": "Jericho Beach",
    "kitsilano-beach": "Kitsilano Beach",
    "wreck-beach": "Wreck Beach",
    "second-beach": "Stanley Park – Second Beach",
    "third-beach": "Stanley Park – Third Beach",
    "iona-beach": "Iona Beach Regional Park",
    "boundary-bay": "Boundary Bay",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/parks-recreation-culture/spanish-banks-beach.aspx",
      label: "City of Vancouver – Spanish Banks",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture/jericho-beach.aspx",
      label: "City of Vancouver – Jericho Beach",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture/kitsilano-beach.aspx",
      label: "City of Vancouver – Kitsilano Beach",
    },
    {
      url: "https://wreckbeach.org/",
      label: "Wreck Beach Preservation Society",
    },
    {
      url: "https://vancouver.ca/parks-recreation-culture/stanley-park.aspx",
      label: "City of Vancouver – Stanley Park",
    },
    {
      url: "http://www.metrovancouver.org/services/parks/parks-greenways-reserves/iona-beach-regional-park",
      label: "Metro Vancouver – Iona Beach Regional Park",
    },
    {
      url: "http://www.metrovancouver.org/services/parks/parks-greenways-reserves/boundary-bay-regional-park",
      label: "Metro Vancouver – Boundary Bay Regional Park",
    },
    {
      url: "https://wildwhales.org/",
      label: "BC Whale Sightings Network",
    },
    {
      url: "https://www.birdscanada.org/",
      label: "Birds Canada",
    },
    {
      url: "https://www.tides.gc.ca/eng",
      label: "Government of Canada – Tides, Currents, and Water Levels",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "spanish-banks",
      lat: 49.2736,
      lng: -123.2202,
      popupText: "Spanish Banks",
    },
    {
      id: "jericho-beach",
      lat: 49.2743,
      lng: -123.2083,
      popupText: "Jericho Beach",
    },
    {
      id: "kitsilano-beach",
      lat: 49.2747,
      lng: -123.1522,
      popupText: "Kitsilano Beach",
    },
    {
      id: "wreck-beach",
      lat: 49.2668,
      lng: -123.2607,
      popupText: "Wreck Beach",
    },
    {
      id: "second-beach",
      lat: 49.2974,
      lng: -123.1462,
      popupText: "Stanley Park – Second Beach",
    },
    {
      id: "third-beach",
      lat: 49.3018,
      lng: -123.1558,
      popupText: "Stanley Park – Third Beach",
    },
    {
      id: "iona-beach",
      lat: 49.2047,
      lng: -123.2072,
      popupText: "Iona Beach Regional Park",
    },
    {
      id: "boundary-bay",
      lat: 49.0045,
      lng: -123.0589,
      popupText: "Boundary Bay / Centennial Beach",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // References to the map
  const mapRef = useRef(null);
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Beaches for Coastal Wildlife</title>
        <meta
          name="description"
          content="Explore Vancouver’s most iconic beaches for coastal wildlife. Discover seals, shorebirds, intertidal life, and more through this practical guide."
        />
        <meta
          name="keywords"
          content="Vancouver, beaches, wildlife, marine life, birdwatching, tides, intertidal, tourism"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-beaches-coastal-wildlife.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Beaches for Coastal Wildlife"
        />
        <meta
          property="og:description"
          content="A practical guide to Vancouver's beaches for birdwatching, marine mammals, and tidal exploration."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-beaches-coastal-wildlife.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-beaches-coastal-wildlife.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Beaches for Coastal Wildlife"
        />
        <meta
          name="twitter:description"
          content="Where to watch shorebirds, seals, and more on Vancouver’s coastline."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-beaches-coastal-wildlife.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={ref_data?.["image-desc"] || "Scenic Vancouver coastline"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is renowned for its stunning coastline, where lush
              forests meet the Pacific Ocean. Along these shores, you'll find
              a thriving array of wildlife: from inquisitive harbor seals and
              playful otters to diverse seabirds and vibrant intertidal
              creatures. Below, we delve into some of the top Vancouver-area
              beaches, each offering unique ecosystems and wildlife-watching
              opportunities. Read on for practical tips, exact coordinates,
              and resources to plan your visit.
            </p>

            {/* SECTION: Spanish Banks */}
            <h2 id="spanish-banks">Spanish Banks</h2>
            <p
              ref={(el) => setSpotRef("spanish-banks", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Spanish Banks</strong>{" "}
              <Marker
                coordY={49.2736}
                coordX={-123.2202}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              is famous for its expansive tidal flats that appear during low
              tide. Here, shorebirds flock to feed on small invertebrates, while
              occasional seals can be spotted hunting in shallow waters. The free
              parking and gentle shoreline make it a family-friendly destination.
              Check the tide tables for the best time to explore the intertidal
              zone.
            </p>

            {/* SECTION: Jericho Beach */}
            <h2 id="jericho-beach">Jericho Beach</h2>
            <p
              ref={(el) => setSpotRef("jericho-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Jericho Beach</strong>{" "}
              <Marker
                coordY={49.2743}
                coordX={-123.2083}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              is a local favorite for spotting waterfowl and migratory birds,
              especially near the adjacent ponds and wetland areas. You might
              catch sight of otters playing along the shoreline. Paid parking
              is available, as are washrooms and nearby walking trails for a
              more extended birdwatching adventure.
            </p>

            {/* SECTION: Kitsilano Beach */}
            <h2 id="kitsilano-beach">Kitsilano Beach</h2>
            <p
              ref={(el) => setSpotRef("kitsilano-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Kitsilano Beach</strong>{" "}
              <Marker
                coordY={49.2747}
                coordX={-123.1522}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              (or “Kits Beach”) is a bustling hub where beachgoers and wildlife
              meet. It’s common to see cormorants, herons, and gulls in the area.
              Seals also make occasional appearances, especially in the quieter
              early morning hours. Although parking is paid, Kits is a convenient
              spot for those staying downtown or looking for more urban amenities.
            </p>

            {/* SECTION: Wreck Beach */}
            <h2 id="wreck-beach">Wreck Beach</h2>
            <p
              ref={(el) => setSpotRef("wreck-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Wreck Beach</strong>{" "}
              <Marker
                coordY={49.2668}
                coordX={-123.2607}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              is well-known as a clothing-optional beach, but it’s also an
              ecological hotspot teeming with bald eagles, ospreys, and
              intertidal species like sea stars and mussels. Access involves
              descending a steep set of stairs from the UBC campus, so come
              prepared with good footwear and essentials, as facilities here
              are minimal.
            </p>

            {/* SECTION: Stanley Park – Second Beach */}
            <h2 id="second-beach">Stanley Park – Second Beach</h2>
            <p
              ref={(el) => setSpotRef("second-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Second Beach</strong>{" "}
              <Marker
                coordY={49.2974}
                coordX={-123.1462}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              in Stanley Park is a popular spot for families, offering a
              swimming pool, playground, and easy access to the Seawall. For
              wildlife enthusiasts, it’s a chance to see harbor seals and a
              variety of seabirds. Combine your visit with a walk around the
              park for an immersive nature experience.
            </p>

            {/* SECTION: Stanley Park – Third Beach */}
            <h2 id="third-beach">Stanley Park – Third Beach</h2>
            <p
              ref={(el) => setSpotRef("third-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Third Beach</strong>{" "}
              <Marker
                coordY={49.3018}
                coordX={-123.1558}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              is another Stanley Park gem. Known for its picturesque views
              at sunset, it’s also less crowded than some other Vancouver
              beaches. Keep an eye out for seabirds, and if you’re lucky,
              you might spot a seal bobbing in the calmer waters offshore.
            </p>

            {/* SECTION: Iona Beach Regional Park */}
            <h2 id="iona-beach">Iona Beach Regional Park</h2>
            <p
              ref={(el) => setSpotRef("iona-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Iona Beach</strong>{" "}
              <Marker
                coordY={49.2047}
                coordX={-123.2072}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              in Richmond sits along the Fraser River estuary, making it a
              prime birdwatching destination. Massive flocks of migratory
              birds pass through in spring and fall. Enjoy a scenic walk along
              the jetty for panoramic views of the coast and the opportunity
              to spot ducks, gulls, and raptors overhead.
            </p>

            {/* SECTION: Boundary Bay */}
            <h2 id="boundary-bay">Boundary Bay / Centennial Beach</h2>
            <p
              ref={(el) => setSpotRef("boundary-bay", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Boundary Bay</strong>{" "}
              <Marker
                coordY={49.0045}
                coordX={-123.0589}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}
              near Tsawwassen is among the best spots in the Lower Mainland
              for birdwatching, particularly during migratory seasons when
              huge flocks of shorebirds arrive. You’ll also find eagles
              perching in nearby trees. Facilities at Centennial Beach
              include parking, washrooms, and a small cafe.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. When is the best time to see coastal wildlife in Vancouver?</h3>
              <p>
                Wildlife can be observed year-round, but low tide offers the best
                intertidal viewing. Early mornings and weekdays tend to be quieter,
                making it easier to spot seals, birds, and other animals without
                large crowds.
              </p>

              <h3>2. Are dogs allowed on these beaches?</h3>
              <p>
                Many Vancouver beaches have seasonal or specific off-leash areas,
                but restrictions vary. Always check local signage and bylaws to
                ensure you’re in compliance.
              </p>

              <h3>3. How can I find tide information?</h3>
              <p>
                Visit the{" "}
                <a
                  href="https://www.tides.gc.ca/eng"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Government of Canada Tide Tables
                </a>{" "}
                or other local tide forecasting services to plan your trip around
                peak intertidal times.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s coastal areas are protected by various municipal and
              provincial regulations. Please:
            </p>
            <ul>
              <li>Keep dogs leashed where required and respect dog off-leash rules.</li>
              <li>Refrain from feeding or disturbing wildlife.</li>
              <li>Pack out all trash and follow “leave no trace” principles.</li>
              <li>Obey posted signage regarding habitat protection zones.</li>
            </ul>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From the tidal flats at Spanish Banks to the secluded shores of
              Wreck Beach, Vancouver’s coastline is a treasure trove of coastal
              wildlife. Whether you're an avid birder, a curious family, or a
              solo traveler seeking natural beauty, these beaches offer a
              unique way to connect with the region’s marine and intertidal
              habitats. Respect these spaces, follow local guidelines, and
              enjoy the rich biodiversity Vancouver has to offer.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}